import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Shield, Target, AlertTriangle, CheckCircle2, XCircle, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

export default function PropFirm() {
  const { t, fmtCurrency } = useApp();
  const [status, setStatus] = useState(null);
  const [presets, setPresets] = useState({});
  const [selected, setSelected] = useState("FTMO_100k");
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const [p, s] = await Promise.all([
      axios.get(`${API}/prop-firm/presets`),
      axios.get(`${API}/prop-firm/status`),
    ]);
    setPresets(p.data.presets);
    setStatus(s.data);
    if (!rules) setRules(p.data.presets[selected]);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (k) => {
    setSelected(k);
    setRules({ ...presets[k] });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/prop-firm/rules`, rules);
      toast.success("Règles Prop Firm enregistrées");
      loadAll();
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  if (!status || !rules) return <div className="p-6 text-white/40">Chargement…</div>;

  const statusColor =
    status.status === "REUSSI" ? "text-up" :
    status.status === "ECHEC" ? "text-down" : "text-accent-blue";
  const StatusIcon =
    status.status === "REUSSI" ? CheckCircle2 :
    status.status === "ECHEC" ? XCircle : Shield;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-3" data-testid="pf-title">
            <Shield size={22} className="text-accent-blue" />
            {t("propfirm.title", "Mode Prop Firm Challenge")}
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Suivez vos règles automatiquement. L'algo vous alerte avant de violer une limite — {status.rules_name}.
          </p>
        </div>
        <div className={`flex items-center gap-2 ${statusColor}`} data-testid="pf-status">
          <StatusIcon size={20} />
          <span className="font-heading text-lg">{status.status}</span>
        </div>
      </div>

      {/* Alerts */}
      {status.violations.length > 0 && (
        <div className="panel p-4 border-down/40 bg-down/5" data-testid="pf-violations">
          <div className="flex items-center gap-2 text-down font-semibold mb-2">
            <XCircle size={16} /> Règles violées
          </div>
          {status.violations.map((v, i) => <div key={i} className="text-sm text-down">{v}</div>)}
        </div>
      )}
      {status.warnings.length > 0 && (
        <div className="panel p-4 border-warn/40 bg-warn/5" data-testid="pf-warnings">
          <div className="flex items-center gap-2 text-warn font-semibold mb-2">
            <AlertTriangle size={16} /> Alertes actives
          </div>
          {status.warnings.map((w, i) => <div key={i} className="text-sm text-warn">{w}</div>)}
        </div>
      )}

      {/* Progress */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="panel p-5" data-testid="pf-progress">
          <div className="panel-title text-[14px] mb-4 flex items-center gap-2">
            <Target size={14} /> Progression vers l'objectif
          </div>
          <div className="mb-3">
            <div className="flex justify-between text-xs text-white/60 mb-1">
              <span>Profit actuel</span>
              <span className={status.total_pnl >= 0 ? "text-up" : "text-down"}>
                {fmtCurrency(status.total_pnl)} ({status.profit_pct}%)
              </span>
            </div>
            <div className="pbar h-3">
              <span style={{
                width: `${Math.min(100, Math.max(0, status.target_progress_pct))}%`,
                background: status.target_progress_pct >= 100 ? "var(--up)" : "var(--accent)"
              }} />
            </div>
            <div className="text-[11px] font-mono text-white/40 mt-1">
              {status.target_progress_pct}% de l'objectif ({fmtCurrency(status.profit_target)})
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MetricCard label="Capital initial" value={fmtCurrency(status.initial_balance)} />
            <MetricCard label="Capital actuel" value={fmtCurrency(status.current_balance)}
                        color={status.current_balance >= status.initial_balance ? "text-up" : "text-down"} />
            <MetricCard label="Jours de trading" value={`${status.trading_days} / ${status.min_trading_days} min`} />
            <MetricCard label="P&L du jour" value={fmtCurrency(status.todays_pnl)}
                        color={status.todays_pnl >= 0 ? "text-up" : "text-down"} />
          </div>
        </div>

        <div className="panel p-5" data-testid="pf-budget">
          <div className="panel-title text-[14px] mb-4">Budget de risque restant</div>
          <div className="space-y-3">
            <RiskBar label="Budget aujourd'hui" used={status.max_daily_loss + status.todays_pnl < 0 ? 0 : Math.max(0, -status.todays_pnl)}
                     limit={status.max_daily_loss} />
            <RiskBar label="Budget total challenge" used={Math.max(0, -status.total_pnl)}
                     limit={status.max_total_loss} />
            <div className="flex items-center justify-between p-3 rounded bg-hover border border-accent-blue/30">
              <div>
                <div className="text-[11px] text-white/50 uppercase tracking-wider">Risque max prochain trade</div>
                <div className="text-lg font-mono text-accent-blue mt-0.5">
                  {fmtCurrency(status.max_next_position?.max_allowed_dollar || 0)}
                </div>
              </div>
              <div className="text-[11px] text-white/50 max-w-[140px] text-right">
                {status.max_next_position?.reason}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rules config */}
      <div className="panel p-5" data-testid="pf-config">
        <div className="panel-title text-[14px] mb-4">Configuration des règles</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(presets).map(([k, p]) => (
            <button
              key={k}
              className={`btn text-xs ${selected === k ? "btn-primary" : ""}`}
              onClick={() => applyPreset(k)}
              data-testid={`preset-${k}`}
            >
              {p.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <RuleField label="Capital initial ($)" k="initial_balance" v={rules.initial_balance} onChange={(v) => setRules({...rules, initial_balance: v})} />
          <RuleField label="Max daily loss (%)" k="max_daily_loss_pct" v={rules.max_daily_loss_pct} onChange={(v) => setRules({...rules, max_daily_loss_pct: v})} step="0.1" />
          <RuleField label="Max total loss (%)" k="max_total_loss_pct" v={rules.max_total_loss_pct} onChange={(v) => setRules({...rules, max_total_loss_pct: v})} step="0.1" />
          <RuleField label="Objectif profit (%)" k="profit_target_pct" v={rules.profit_target_pct} onChange={(v) => setRules({...rules, profit_target_pct: v})} step="0.1" />
          <RuleField label="Jours min. trading" k="min_trading_days" v={rules.min_trading_days} onChange={(v) => setRules({...rules, min_trading_days: v})} />
          <RuleField label="Jours max. challenge" k="max_days" v={rules.max_days} onChange={(v) => setRules({...rules, max_days: v})} />
        </div>
        <button className="btn btn-primary mt-4" onClick={save} disabled={saving} data-testid="pf-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer les règles
        </button>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color = "text-white" }) {
  return (
    <div className="bg-hover rounded px-3 py-2 border border-white/5">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-mono text-sm mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function RiskBar({ label, used, limit }) {
  const { fmtCurrency } = useApp();
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const color = pct >= 80 ? "var(--down)" : pct >= 60 ? "var(--warn)" : "var(--up)";
  return (
    <div>
      <div className="flex justify-between text-xs text-white/60 mb-1">
        <span>{label}</span>
        <span className="font-mono">{fmtCurrency(used)} / {fmtCurrency(limit)}</span>
      </div>
      <div className="pbar h-2.5">
        <span style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function RuleField({ label, k, v, onChange, step }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input type="number" step={step || "1"} className="field" value={v}
             onChange={(e) => onChange(+e.target.value)}
             data-testid={`pf-rule-${k}`} />
    </div>
  );
}
